import apiClient from '../client';
import {
  Customer,
  CustomerRegistrationRequest,
  CustomerUpdateRequest,
  KYCDocumentUpload,
  KYCVerificationRequest,
  CreditScoreResponse,
  LoanLimitResponse,
  BlockCustomerRequest,
  CustomerListParams,
  CustomerListResponse,
} from '../types/customer.types';

/**
 * Customer API endpoints
 */
export const customersApi = {
  /**
   * Register a new customer
   */
  register: async (data: CustomerRegistrationRequest): Promise<{ customerId: string; customer: Customer }> => {
    const response = await apiClient.post<{ customerId: string; customer: Customer }>(
      '/customers/register',
      data
    );
    return response.data;
  },

  /**
   * Get customer details by ID
   */
  getById: async (customerId: string): Promise<Customer> => {
    const response = await apiClient.get<{ customer: Customer }>(
      `/customers/${customerId}`
    );
    return response.data.customer;
  },

  /**
   * Update customer profile
   */
  update: async (customerId: string, data: CustomerUpdateRequest): Promise<Customer> => {
    const response = await apiClient.put<{ customer: Customer }>(
      `/customers/${customerId}`,
      data
    );
    return response.data.customer;
  },

  /**
   * Upload KYC documents
   */
  uploadKYC: async (customerId: string, data: KYCDocumentUpload): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/customers/${customerId}/upload-kyc`,
      data
    );
    return response.data;
  },

  /**
   * Verify KYC
   */
  verifyKYC: async (customerId: string, data: KYCVerificationRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/customers/${customerId}/verify-kyc`,
      data
    );
    return response.data;
  },

  /**
   * Get customer credit score
   */
  getCreditScore: async (customerId: string): Promise<CreditScoreResponse> => {
    const response = await apiClient.get<CreditScoreResponse>(
      `/customers/${customerId}/credit-score`
    );
    return response.data;
  },

  /**
   * Get customer loan limit
   */
  getLoanLimit: async (customerId: string): Promise<LoanLimitResponse> => {
    const response = await apiClient.get<LoanLimitResponse>(
      `/customers/${customerId}/loan-limit`
    );
    return response.data;
  },

  /**
   * List all customers
   */
  list: async (params?: CustomerListParams): Promise<CustomerListResponse> => {
    const response = await apiClient.get<CustomerListResponse>('/customers', { params });
    return response.data;
  },

  /**
   * Search customers
   */
  search: async (query: string): Promise<Customer[]> => {
    const response = await apiClient.get<{ customers: Customer[] }>(
      '/customers/search',
      { params: { q: query } }
    );
    return response.data.customers;
  },

  /**
   * Block a customer
   */
  block: async (customerId: string, data: BlockCustomerRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/customers/${customerId}/block`,
      data
    );
    return response.data;
  },

  /**
   * Unblock a customer
   */
  unblock: async (customerId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/customers/${customerId}/unblock`
    );
    return response.data;
  },
};
